// ============================================================================
// The Pipeline (2026-10-16): every change from a ready pull request to prod,
// lane by lane, with what is stuck said in words and the one safe fix beside
// it. The owner's ask: "better visibility of what's ready to merge, currently
// merging and deploying, and what's in the merge queue; see exactly what's
// deploying and what's getting stuck." Prime reads the same lanes (MCP
// `pipeline`), so what it says matches this screen.
//
// Online-only: GET /api/pipeline every 4 s while anything moves, else every
// 30 s, and only while the page is visible. The lanes are worked out here
// (lib/flow.ts) from that answer and the device's own mirrors and merge cards,
// so a tap on Merge moves its row as soon as the card syncs. The first load
// shows Skeletons, a failed one Failed; after that a failed poll keeps what is
// on screen (mid-deploy it is prod restarting, as ShipPipeline reads it).
//
// Merge batches (2026-10-18, docs/decisions.md "Merges go in batches"): cards
// queued together are one batch, drawn as one block with its summary line
// ("Merging 6: 3 in, 1 updating, 2 waiting") and its pull requests beneath,
// instead of among the queued and merging rows. Merge and ship beside Merge
// all queues the ready ones as a batch that releases main once it has ended.
// ============================================================================
import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { api } from '@/runtime'
import type { PipelineAnswer } from '@/db/api'
import { useFlowRecords } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { flowMark } from '@/lib/colour'
import {
  FIXES,
  MAX_LANE_SHOWN,
  flowAgent,
  flowCard,
  flowOf,
  flowPR,
  inMotion,
  laneOf,
  mergeable,
  prKey,
  type BatchMember,
  type FixKey,
  type Flow,
  type FlowAgent,
  type FlowBatch,
  type FlowCard,
  type FlowItem,
  type FlowLane,
  type FlowPR,
} from '@/lib/flow'
import { useUi } from '@/state/ui'
import { ShipSheet } from '@/views/dev/Releases'
import { ShipPipeline } from './ShipPipeline'
import { Skeleton } from './Skeleton'
import { Button, Failed, SectionTitle, cx } from './ui'

// ---- reading it --------------------------------------------------------------------

/** Polls GET /api/pipeline: every 4 s while its lanes say something moves, else every 30 s. */
export const usePipelineAnswer = () => {
  const [answer, setAnswer] = useState<PipelineAnswer | null>(null)
  const [error, setError] = useState('')
  const [unreachable, setUnreachable] = useState(false)
  const [tick, setTick] = useState(0)
  const had = !!answer
  const fast = !!answer && inMotion({ lanes: answer.lanes })
  useEffect(() => {
    let stale = false
    const load = () =>
      api
        .pipeline()
        .then((a) => {
          if (stale) return
          setAnswer(a)
          setError('')
          setUnreachable(false)
        })
        .catch((e: Error) => {
          if (stale) return
          // once something is on screen it stays: a poll that fails mid-deploy is prod restarting
          if (had) setUnreachable(true)
          else setError(e.message)
        })
    void load()
    const t = window.setInterval(
      () => document.visibilityState === 'visible' && void load(),
      fast ? 4_000 : 30_000,
    )
    return () => {
      stale = true
      window.clearInterval(t)
    }
  }, [tick, fast, had])
  return { answer, error, unreachable, fast, reload: () => setTick((n) => n + 1) }
}

/** The answer and the device's records made into lanes (lib/flow.ts). */
const flowFrom = (
  a: PipelineAnswer,
  now: number,
  records: { prs: FlowPR[]; agents: FlowAgent[]; cards: FlowCard[] },
): Flow => {
  const up = a.upstream
  // what Forgejo said just now of an open PR whose change shows on main wins over a mirror that lags
  const checked = new Map((up.checked ?? []).map((p) => [prKey(p.repo, p.number), p]))
  const prs = records.prs.map((p) => checked.get(prKey(p.repo, p.number)) ?? p)
  for (const [k, p] of checked) if (!records.prs.some((q) => prKey(q.repo, q.number) === k)) prs.push(p)
  return flowOf({
    now: new Date(now).toISOString(),
    repo: up.repo,
    prs,
    agents: records.agents,
    cards: records.cards,
    builds: up.builds,
    releases: up.releases.map((r) => ({ tag: r.tag, sha: r.sha, at: r.at })),
    prod: up.versions.prod ?? '',
    staging: up.versions.staging ?? '',
    main: up.main,
    ...(up.main_at ? { main_at: up.main_at } : {}),
    pods: (up.rollout?.pods ?? []).map((p) => ({
      image: p.image,
      ready: p.ready,
      ...(p.terminating ? { terminating: true } : {}),
      ...(p.waiting ? { waiting: p.waiting } : {}),
    })),
  })
}

/** Everything the screen draws: the lanes, worked out on this device, and the answer under them. */
export const usePipeline = () => {
  const records = useFlowRecords()
  const { answer, error, unreachable, fast, reload } = usePipelineAnswer()
  const now = useNow(fast ? 1_000 : 30_000)
  const flow = useMemo(() => {
    if (!answer) return null
    const recs = {
      prs: (records?.prs ?? []).map(flowPR).filter((p): p is FlowPR => !!p),
      agents: (records?.runs ?? []).map((r) => flowAgent(r)).filter((r): r is FlowAgent => !!r),
      cards: (records?.cards ?? []).map(flowCard).filter((c): c is FlowCard => !!c),
    }
    return flowFrom(answer, now, recs)
  }, [answer, records, now])
  return { flow, answer, error, unreachable, reload, now }
}

// ---- drawing it --------------------------------------------------------------------

/** How long a row has been in its lane: seconds while it is short, then minutes, hours, days. */
export const inLane = (since: string | undefined, now: number) => {
  if (!since) return ''
  const s = Math.max(0, Math.round((now - Date.parse(since)) / 1000))
  if (Number.isNaN(s)) return ''
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 48) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

const MOVING = new Set(['queued', 'merging', 'building', 'staging', 'deploying'])

const Dot = ({ lane, stuck }: { lane: string; stuck: boolean }) => (
  <span
    className={cx(
      'mt-[7px] size-2 rounded-full shrink-0',
      stuck
        ? flowMark.stuck
        : lane === 'live'
          ? flowMark.live
          : MOVING.has(lane)
            ? `${flowMark.moving} ship-pulse`
            : flowMark.waiting,
    )}
    aria-hidden
  />
)

interface RowActs {
  merge?: (it: FlowItem) => void
  fix: (it: FlowItem, fix: FixKey) => void
  busy: Record<string, string>
  said: Record<string, string>
  /** the repo that ships: another repo's rows name theirs */
  home: string
}

const BEFORE_MERGE = new Set(['ready', 'queued', 'merging'])

const Row = ({ it, lane, now, acts }: { it: FlowItem; lane: string; now: number; acts: RowActs }) => {
  const title = (
    <span className="text-[15px] leading-snug line-clamp-2">
      {it.number ? <span className="font-mono text-[12px] text-faint mr-1.5">#{it.number}</span> : null}
      {it.title}
    </span>
  )
  const busy = acts.busy[it.key]
  const said = acts.said[it.key]
  const linkClass = 'block hover:underline underline-offset-2'
  return (
    <li className="px-3 py-2.5" data-testid="pipeline-row" data-key={it.key} data-stuck={!!it.stuck}>
      <div className="flex items-start gap-2.5">
        <Dot lane={lane} stuck={!!it.stuck} />
        <div className="flex-1 min-w-0">
          {/* a pull request before it merges opens its review, after it its agent */}
          {it.agent && BEFORE_MERGE.has(lane) ? (
            <Link to="/dev/review/$id" params={{ id: it.agent }} className={linkClass}>
              {title}
            </Link>
          ) : it.agent ? (
            <Link to="/agents/run/$id" params={{ id: it.agent }} className={linkClass}>
              {title}
            </Link>
          ) : (
            title
          )}
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-0.5 text-xs text-muted">
            {it.agent_title && <span className="truncate max-w-[14rem]">{it.agent_title}</span>}
            {it.repo && it.repo !== acts.home ? (
              <span className="font-mono text-faint">{it.repo}</span>
            ) : null}
            {it.sha && <span className="font-mono text-faint">{it.sha.slice(0, 7)}</span>}
            {it.note && <span>{it.note}</span>}
            {it.since && (
              <span className="text-faint tabular-nums" title={new Date(it.since).toLocaleString()}>
                {inLane(it.since, now)}
              </span>
            )}
          </span>
          {it.stuck && (
            <p
              className={cx('text-[13px] leading-snug mt-1', flowMark.stuckText)}
              data-testid="pipeline-stuck"
            >
              {it.stuck.reason}
            </p>
          )}
          {said && <p className="text-[13px] leading-snug mt-1 text-fg-2">{said}</p>}
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {it.stuck?.fix && (
            <Button
              variant="chip"
              disabled={!!busy}
              onClick={() => it.stuck?.fix && acts.fix(it, it.stuck.fix)}
              data-testid="pipeline-fix"
            >
              {busy === 'fix' ? 'Fixing…' : FIXES[it.stuck.fix]}
            </Button>
          )}
          {lane === 'ready' && !it.stuck && acts.merge && (
            <Button
              variant="chip"
              disabled={!!busy}
              onClick={() => acts.merge?.(it)}
              data-testid="pipeline-merge"
            >
              {busy === 'merge' ? 'Queuing…' : 'Merge'}
            </Button>
          )}
        </div>
      </div>
    </li>
  )
}

const LaneList = ({ lane, now, acts }: { lane: FlowLane; now: number; acts: RowActs }) => {
  const [all, setAll] = useState(false)
  const shown = all ? lane.items : lane.items.slice(0, MAX_LANE_SHOWN)
  return (
    <>
      <ul className="group-list">
        {shown.map((it) => (
          <Row key={it.key} it={it} lane={lane.key} now={now} acts={acts} />
        ))}
      </ul>
      {!all && lane.items.length > MAX_LANE_SHOWN && (
        <button type="button" className="text-xs text-muted mt-1.5 px-1" onClick={() => setAll(true)}>
          Show the rest
        </button>
      )}
    </>
  )
}

/** The fixes and merges a screen can do, with what each said. */
const useActs = (reload: () => void) => {
  const [busy, setBusy] = useState<Record<string, string>>({})
  const [said, setSaid] = useState<Record<string, string>>({})
  const mark = (key: string, what: string | null) =>
    setBusy((b) => {
      const n = { ...b }
      if (what) n[key] = what
      else delete n[key]
      return n
    })
  const say = (key: string, words: string) => setSaid((s) => ({ ...s, [key]: words }))
  const fix = async (it: FlowItem, f: FixKey) => {
    mark(it.key, 'fix')
    try {
      const r = await api.pipelineFix(it.key, f)
      say(it.key, r.done)
      useUi.getState().showFlash(r.done, 4000)
      reload()
    } catch (e) {
      say(it.key, (e as Error).message)
    } finally {
      mark(it.key, null)
    }
  }
  const merge = async (items: FlowItem[], ship = false) => {
    for (const it of items) mark(it.key, ship ? 'ship' : 'merge')
    try {
      const r = await api.pipelineMerge(
        items.map((i) => i.key),
        ship,
      )
      for (const res of r.results) if (res.error) say(res.item, res.error)
      reload()
    } catch (e) {
      for (const it of items) say(it.key, (e as Error).message)
    } finally {
      for (const it of items) mark(it.key, null)
    }
  }
  return { busy, said, fix, merge }
}

const MEMBER_WORDS: Record<BatchMember['state'], string> = {
  merged: 'In',
  merging: 'Merging',
  updating: 'Updating',
  queued: 'Waiting',
  failed: 'Failed',
}

/** A member's dot: merged is done, failed needs the owner, the rest are on their way. */
const memberMark = (st: BatchMember['state']) =>
  st === 'merged'
    ? flowMark.live
    : st === 'failed'
      ? flowMark.stuck
      : st === 'queued'
        ? flowMark.waiting
        : `${flowMark.moving} ship-pulse`

/**
 * A batch's pull request that has no row of its own (it merged, or the mirror
 * has not caught up): its number, title and where it stands.
 */
const MemberRow = ({ m }: { m: BatchMember }) => (
  <li className="px-3 py-2.5" data-testid="pipeline-batch-member" data-state={m.state}>
    <div className="flex items-start gap-2.5">
      <span className={cx('mt-[7px] size-2 rounded-full shrink-0', memberMark(m.state))} aria-hidden />
      <span className="flex-1 min-w-0 text-[15px] leading-snug line-clamp-2">
        <span className="font-mono text-[12px] text-faint mr-1.5">#{m.number}</span>
        {m.title}
      </span>
      <span className="shrink-0 text-xs text-muted mt-0.5">{MEMBER_WORDS[m.state]}</span>
    </div>
  </li>
)

/**
 * One merge batch: its summary as the section's label ("Merging and shipping
 * 6: …" when it ships), and its pull requests beneath, the ones still in flight as their full rows
 * (with any fix), the merged ones as a line each.
 */
const BatchBlock = ({
  batch,
  rows,
  now,
  acts,
}: {
  batch: FlowBatch
  rows: Map<string, { it: FlowItem; lane: string }>
  now: number
  acts: RowActs
}) => (
  <section className="mb-5" data-testid="pipeline-batch" data-batch={batch.id}>
    <SectionTitle>{batch.summary}</SectionTitle>
    <ul className="group-list">
      {batch.members.map((m) => {
        const row = rows.get(m.key)
        // out of their lanes the rows say where they are: Merging, Updating's note, nth in line
        return row ? (
          <Row
            key={m.key}
            it={row.it.note ? row.it : { ...row.it, note: MEMBER_WORDS[m.state] }}
            lane={row.lane}
            now={now}
            acts={acts}
          />
        ) : (
          <MemberRow key={m.key} m={m} />
        )
      })}
    </ul>
  </section>
)

/** The lanes in full: the Pipeline screen. */
export const PipelineLanes = () => {
  const { flow, answer, error, unreachable, reload, now } = usePipeline()
  const { busy, said, fix, merge } = useActs(reload)
  const [ship, setShip] = useState(false)
  if (!flow || !answer) {
    if (error)
      return (
        <Failed
          what="the pipeline"
          reason={error}
          onRetry={reload}
          hint={error.includes('not set up') ? 'Builds need the factory on this server.' : undefined}
        />
      )
    return (
      <div className="flex flex-col gap-5" data-testid="skeleton" aria-busy="true" aria-label="Loading">
        {[3, 2, 2].map((n, i) => (
          <div key={i}>
            <Skeleton className="h-4 w-32 mb-2" />
            <Skeleton className={n === 3 ? 'h-40' : 'h-24'} />
          </div>
        ))}
      </div>
    )
  }
  const ready = mergeable(flow)
  // Merge and ship releases what the repo that builds here merges
  const shippable = ready.filter((it) => it.repo === answer.upstream.repo)
  const batches = flow.batches ?? []
  // a batch's rows are drawn in its block, not among the queued and merging ones
  const inBatch = new Set(batches.flatMap((b) => b.members.map((m) => m.key)))
  const rows = new Map<string, { it: FlowItem; lane: string }>()
  for (const l of flow.lanes)
    if (BEFORE_MERGE.has(l.key))
      for (const it of l.items) if (inBatch.has(it.key)) rows.set(it.key, { it, lane: l.key })
  const lanes = flow.lanes.map((l) =>
    BEFORE_MERGE.has(l.key) ? { ...l, items: l.items.filter((it) => !inBatch.has(it.key)) } : l,
  )
  const acts: RowActs = {
    fix: (it, f) => void fix(it, f),
    merge: (it) => void merge([it]),
    busy,
    said,
    home: answer.upstream.repo,
  }
  const full = lanes.filter((l) => l.items.length > 0)
  const empty = lanes.filter((l) => l.items.length === 0 && !(BEFORE_MERGE.has(l.key) && batches.length > 0))
  const before = full.filter((l) => l.key === 'ready' || l.key === 'queued' || l.key === 'merging')
  const after = full.filter((l) => !before.includes(l))
  const waitingTop = laneOf(flow, 'waiting')[0]
  const section = (l: FlowLane) => (
    <section key={l.key} className="mb-5" data-testid={`pipeline-lane-${l.key}`}>
      <SectionTitle
        right={
          l.key === 'ready' && (ready.length > 1 || shippable.length > 0) ? (
            <span className="flex items-center gap-1.5">
              {ready.length > 1 && (
                <Button
                  variant="chip"
                  disabled={ready.some((it) => !!busy[it.key])}
                  onClick={() => void merge(ready)}
                  data-testid="pipeline-merge-all"
                >
                  {ready.some((it) => busy[it.key] === 'merge') ? 'Queuing…' : 'Merge all'}
                </Button>
              )}
              {shippable.length > 0 && (
                <Button
                  variant="chipOn"
                  disabled={ready.some((it) => !!busy[it.key])}
                  onClick={() => void merge(shippable, true)}
                  data-testid="pipeline-merge-ship"
                >
                  {shippable.some((it) => busy[it.key] === 'ship') ? 'Queuing…' : 'Merge and ship'}
                </Button>
              )}
            </span>
          ) : l.key === 'waiting' && flow.ship ? (
            <Button variant="chipOn" onClick={() => setShip(true)} data-testid="pipeline-ship-all">
              Ship all
            </Button>
          ) : undefined
        }
      >
        {l.label}
      </SectionTitle>
      {l.key === 'deploying' && (
        <div className="mb-2">
          <ShipPipeline builds={answer.upstream} unreachable={unreachable} />
        </div>
      )}
      <LaneList lane={l} now={now} acts={acts} />
    </section>
  )
  return (
    <div data-testid="pipeline" data-moving={inMotion(flow)}>
      {full.length === 0 && batches.length === 0 ? (
        <p className="text-sm text-muted px-1 mb-5" data-testid="pipeline-empty">
          Nothing in flight: no pull request is ready or merging, and prod runs main's newest build.
        </p>
      ) : (
        <div className="xl:grid xl:grid-cols-2 xl:gap-6 xl:items-start">
          <div>
            {batches.map((b) => (
              <BatchBlock key={b.id} batch={b} rows={rows} now={now} acts={acts} />
            ))}
            {before.map(section)}
          </div>
          <div>{after.map(section)}</div>
        </div>
      )}
      {empty.length > 0 && (full.length > 0 || batches.length > 0) && (
        <p className="text-xs text-faint px-1" data-testid="pipeline-quiet">
          Nothing {empty.map((l) => l.label.charAt(0).toLowerCase() + l.label.slice(1)).join(', ')}.
        </p>
      )}
      {ship && flow.ship && (
        <ShipSheet
          sha={flow.ship}
          note={waitingTop?.title}
          back={false}
          onClose={() => setShip(false)}
          onDone={() => {
            setShip(false)
            reload()
          }}
        />
      )}
    </div>
  )
}

// ---- the compact form, on DevOS's Now page -----------------------------------------------

const COMPACT_LANES = new Set(['ready', 'queued', 'merging', 'building', 'staging', 'waiting'])

/**
 * Under the ship stages on the Now page: each lane before prod that has
 * anything, named, with its first few rows and anything stuck in amber; the
 * deploy itself is ShipPipeline above it. Nothing when nothing is in flight.
 */
export const PipelineCompact = () => {
  const { flow, now } = usePipeline()
  if (!flow) return null
  const lanes = flow.lanes.filter((l) => COMPACT_LANES.has(l.key) && l.items.length > 0)
  if (lanes.length === 0) return null
  return (
    <div className="card p-3 mb-2" data-testid="pipeline-compact">
      <div className="flex items-baseline mb-1.5">
        <span className="font-semibold">Pipeline</span>
        <Link to="/agents/pipeline" className="ml-auto text-xs text-muted">
          Open
        </Link>
      </div>
      {lanes.map((l) => (
        <div key={l.key} className="mt-2 first:mt-0">
          <p className="label text-muted mb-0.5">{l.label}</p>
          <ul className="flex flex-col gap-1">
            {l.items.slice(0, 3).map((it) => (
              <li key={it.key} className="flex items-start gap-2 text-sm leading-5">
                <Dot lane={l.key} stuck={!!it.stuck} />
                <span className="flex-1 min-w-0">
                  <span className="line-clamp-1">
                    {it.number ? (
                      <span className="font-mono text-[12px] text-faint mr-1">#{it.number}</span>
                    ) : null}
                    {it.title}
                  </span>
                  {it.stuck && (
                    <span className={cx('block text-xs line-clamp-2', flowMark.stuckText)}>
                      {it.stuck.reason}
                    </span>
                  )}
                </span>
                <span className="text-[11px] text-faint tabular-nums">{inLane(it.since, now)}</span>
              </li>
            ))}
          </ul>
          {l.items.length > 3 && (
            <Link to="/agents/pipeline" className="text-xs text-muted">
              And the rest
            </Link>
          )}
        </div>
      ))}
    </div>
  )
}
